import { useQueryClient, onlineManager } from '@tanstack/react-query';
import React, { createContext, useState, Context, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { authEventEmitter } from '..';
import { useApiSDK } from './apiSdkContext';
import { getEntityContent, TEntities } from '../helpers/entities';
import { setRequestRefresh } from '../helpers/refresh-rpc';
import { useRefreshTokens } from '../hooks/access-tokens/refresh-tokens';
import { useLogin } from '../hooks/auth/login';
import { useLogout } from '../hooks/auth/logout';
import { useGetUser } from '../hooks/users/get-me';

/* --------------------------------- Consts --------------------------------- */

// Jednotné TTL pro access token (slaď s backendem)
const ACCESS_TOKEN_TTL_MS: number = 10 * 60 * 1000; // 10 min
const PROACTIVE_REFRESH_MS: number = 5 * 60 * 1000; // 5 min
const INACTIVITY_LIMIT: number = 10 * 60 * 1000; // 10 min
const ACTIVITY_WRITE_THROTTLE_MS: number = 3000;
const API_DOWN_RETRY_MS: number = 60_000; // 60s backoff na outage
const MAX_REFRESH_ATTEMPTS: number = 3;
const BACKOFF_KEY: string = 'authRefreshBackoffUntil'; // epoch ms until next refresh attempt

/* ----------------------------- Safe env helpers ---------------------------- */
const isBrowser: boolean = typeof window !== 'undefined';
const now = (): number => Date.now();

function lsGet(key: string): string | null {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function lsSet(key: string, value: string): void {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
function lsRemove(key: string): void {
  if (!isBrowser) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
function lsGetInt(key: string, fallback: number = 0): number {
  const raw = lsGet(key);
  if (!raw) return fallback;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

/* ---------------------------- Broadcast fallback --------------------------- */

type TChannelMsg =
  | { type: 'FORCE_LOGOUT' }
  | { type: 'FORCE_LOGIN'; accessToken: string }
  | { type: 'SERVER_DOWN'; until: number };

function createChannel(name: string): BroadcastChannel | null {
  if (!isBrowser) return null as BroadcastChannel | null;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      return new BroadcastChannel(name);
    }
  } catch {
    // ignore
  }
  return null;
}

const AUTH_CHANNEL: BroadcastChannel | null = createChannel('auth_channel');

/* --------------------------------- Types ---------------------------------- */

type TRefreshResult = 'ok' | 'degraded' | 'logout';

type TLoggedUser = {
  userId: string;
  first_name: string;
  last_name: string;
  role: string;
  isPasswordChangeRequired: boolean;
  email: string;
};

type TAuthContextValue = {
  isLoggedIn: boolean;
  authReady: boolean;
  inactivity: boolean;
  loginError: boolean;
  handleSetLoginError: (isError: boolean) => void;
  logIn: (username: string, password: string) => Promise<boolean>;
  logOut: (shouldRedirectToCurrentPath?: boolean) => void;
  isActionAllowed: (resource: TEntities['entitySingular'], action: 'read' | 'create' | 'update' | 'delete') => boolean;
  permissions: string[] | undefined;
  user: TLoggedUser | undefined;
  loginErrorMsg: string | undefined;
  handleSetLoginErrorMsg: (errorMsg: string | undefined) => void;

  // Degraded/outage helpers for UI
  serverDownUntil: number;
  isServerDown: boolean;
  authDegraded: boolean;
};

const AuthContext: Context<TAuthContextValue> = createContext<TAuthContextValue>({
  isLoggedIn: false,
  authReady: false,
  inactivity: false,
  loginError: false,
  handleSetLoginError: () => {},
  logIn: async () => false,
  logOut: () => {},
  isActionAllowed: () => false,
  permissions: undefined,
  user: undefined,
  loginErrorMsg: undefined,
  handleSetLoginErrorMsg: () => {},

  serverDownUntil: 0,
  isServerDown: false,
  authDegraded: false,
});

type TAuthContextProviderProps = {
  children: React.ReactNode;
};

const AuthContextProvider = ({ children }: TAuthContextProviderProps): JSX.Element => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync } = useLogin();
  const { mutate: logOutMutate } = useLogout();
  const { refreshTokenMutation: refresh } = useRefreshTokens();

  const sdk = useApiSDK();

  /* ------------------------------- Refs/State ------------------------------ */

  const hasFetched = useRef(false);
  const inactivityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWrite = useRef(0);
  const accessTokenRef = useRef<string | undefined>(undefined);
  const accessTokenExpRef = useRef(0);

  // mutex na refresh
  const refreshInFlightRef = useRef<Promise<TRefreshResult> | null>(null);

  const [authReady, setAuthReady] = useState(false);
  const [permissions, setPermissions] = useState<string[] | undefined>();
  const [user, setUser] = useState<TLoggedUser | undefined>();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [inactivity, setInactivity] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<boolean>(false);
  const [loginErrorMsg, setLoginErrorMsg] = useState<string | undefined>(undefined);

  // Degraded state + outage window
  const [serverDownUntil, setServerDownUntil] = useState<number>(lsGetInt(BACKOFF_KEY, 0));
  const isServerDown = serverDownUntil > now();
  const [authDegraded, setAuthDegraded] = useState<boolean>(false);

  const { isLoading: userIsLoading, isError: userIsError, data: userData } = useGetUser(isLoggedIn);

  /* ------------------------- USER DATA + PERMISSIONS ----------------------- */

  useEffect(() => {
    if (userData && !userIsLoading && !userIsError && isLoggedIn) {
      setPermissions(userData.data?.permissions);
      try {
        const id = userData.data.user_id;
        const first_name = userData.data.first_name;
        const last_name = userData.data.last_name;
        const role = userData.data.role.name;
        const isPasswordChangeRequired = userData.data.password_change_required;
        const email = userData.data.email;
        setUser({ userId: id, first_name, last_name, role, isPasswordChangeRequired, email });
      } catch (error) {
        console.error(error);
      }
    }
    if (!isLoggedIn) {
      setUser(undefined);
      setPermissions(undefined);
    }
  }, [userData, isLoggedIn, userIsLoading, userIsError]);

  /* --------- Pause/Resume React Query během outage/degraded módu ----------- */

  useEffect(() => {
    const offline = isServerDown || authDegraded;
    try {
      onlineManager.setOnline(!offline);
    } catch {
      /* ignore */
    }

    // Idempotentní nastavení jen těch voleb, které potřebujeme ovlivnit
    const currentOptions = queryClient.getDefaultOptions();
    queryClient.setDefaultOptions({
      ...currentOptions,
      queries: {
        ...currentOptions.queries,
        retry: (failureCount: number, _error: any) => {
          if (offline) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: !offline,
        refetchOnReconnect: !offline,
      },
    });
  }, [isServerDown, authDegraded, queryClient]);

  /* -------------------------- Token / refresh utils ------------------------ */

  const scheduleRefresh = useCallback(() => {
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
      refreshTimeout.current = null;
    }

    const exp = accessTokenExpRef.current;
    if (!exp) {
      return;
    }

    const backoffUntil = lsGetInt(BACKOFF_KEY, 0);
    if (now() < backoffUntil) {
      return; // pauza během outage
    }

    const delay = Math.max(exp - now() - PROACTIVE_REFRESH_MS, 0);
    const safeDelay = Math.max(delay, 5_000); // vyhnout se okamžitému bouchnutí
    refreshTimeout.current = setTimeout(() => authEventEmitter.emit('refreshTokens'), safeDelay);
  }, []);

  const updateAuthToken = useCallback(
    (token: string) => {
      accessTokenRef.current = token;
      accessTokenExpRef.current = now() + ACCESS_TOKEN_TTL_MS;
      sdk.setAuthToken(token);
      setAuthDegraded(false);
      scheduleRefresh();
    },
    [sdk, scheduleRefresh]
  );

  const isActionAllowed = useCallback(
    (resource: TEntities['entitySingular'], action: 'read' | 'create' | 'update' | 'delete') => {
      if (!permissions) {
        return false;
      }

      const resourceKey = getEntityContent(resource).key;
      return permissions.includes(`${resourceKey}:${action}`);
    },
    [permissions]
  );

  const handleSetLoginError = useCallback((isError: boolean) => {
    setLoginError(isError);
    if (!isError) setLoginErrorMsg(undefined);
  }, []);

  const handleSetLoginErrorMsg = useCallback((msg?: string) => {
    setLoginErrorMsg(msg);
  }, []);

  const logOutCleanup = useCallback((): void => {
    accessTokenRef.current = undefined;
    accessTokenExpRef.current = 0;
    sdk.clearAuthToken();
    // Remove values left by older frontend versions.
    lsRemove('accessToken');
    lsRemove('accessTokenExp');
    lsRemove('refreshToken');
    lsRemove('lastAddedPoint');
    lsRemove('lastActivityTime');
    lsRemove('companyId');
    lsRemove('areaId');
    queryClient.clear();
  }, [sdk, queryClient]);

  // Používat POUZE pro 4xx / explicitní logout (ne pro 5xx/network)
  const applyLoggedOutState = useCallback(
    (shouldRedirect = true) => {
      setIsLoggedIn(false);
      const accessToken = accessTokenRef.current;
      if (accessToken && !lsGet('isLoggingOut')) {
        logOutMutate(accessToken);
      }
      logOutCleanup();
      if (shouldRedirect && isBrowser) {
        const currentPath = window.location.pathname + window.location.search;
        window.location.replace(`/login?redirectUrl=${encodeURIComponent(currentPath)}`);
      }
    },
    [logOutMutate, logOutCleanup]
  );

  const logOut = useCallback(
    (shouldRedirectToCurrentPath = false) => {
      console.log('User logging out...');

      const isInitiator = !lsGet('isLoggingOut');
      if (isInitiator) {
        lsSet('isLoggingOut', 'true'); // signal ostatním tabům
        const msg: TChannelMsg = { type: 'FORCE_LOGOUT' };
        AUTH_CHANNEL?.postMessage(msg);
        // Fallback pro případy bez BroadcastChannel
        lsSet('bc_event', JSON.stringify(msg)); // storage event pro ostatní taby
        setTimeout(() => lsRemove('bc_event'), 200);
      }

      setIsLoggedIn(false);
      const accessToken = accessTokenRef.current;
      if (accessToken) logOutMutate(accessToken);
      logOutCleanup();

      setTimeout(() => lsRemove('isLoggingOut'), 1000);

      if (isBrowser) {
        setTimeout(() => {
          const currentPath = window.location.pathname + window.location.search;
          window.location.replace(
            shouldRedirectToCurrentPath ? `/login?redirectUrl=${encodeURIComponent(currentPath)}` : `/login`
          );
        }, 0);
      }
    },
    [logOutMutate, logOutCleanup]
  );

  const logIn = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      setInactivity(false);
      try {
        const data = await mutateAsync({ username, password });
        if (data) {
          updateAuthToken(data.accessToken);
          lsSet('refreshToken', data.refreshToken);
          setIsLoggedIn(true);
          await queryClient.invalidateQueries({ queryKey: ['companies'] });

          const msg: TChannelMsg = {
            type: 'FORCE_LOGIN',
            accessToken: data.accessToken,
          };
          AUTH_CHANNEL?.postMessage(msg);

          return true;
        }
      } catch (err: any) {
        if (err?.response?.status === 401) handleSetLoginErrorMsg(t('error_codes.login'));
        else if (err?.response?.status === 500) handleSetLoginErrorMsg(t('error_codes.server'));
        else handleSetLoginErrorMsg(`${err?.response?.statusText ?? 'Error'} - ${err?.message}`);
        setLoginError(true);
      }
      return false;
    },
    [mutateAsync, t, updateAuthToken, queryClient, handleSetLoginErrorMsg]
  );

  const handleRefreshToken = useCallback(async (): Promise<TRefreshResult> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const task: Promise<TRefreshResult> = (async (): Promise<TRefreshResult> => {
      try {
        const refreshToken = lsGet('refreshToken');
        if (!refreshToken) {
          applyLoggedOutState(false);
          return 'logout';
        }

        const backoffUntil = lsGetInt(BACKOFF_KEY, 0);
        if (now() < backoffUntil) {
          setServerDownUntil(backoffUntil);
          setAuthDegraded(true);
          setIsLoggedIn(true);
          return 'degraded';
        }

        let attempt = 0;
        while (attempt < MAX_REFRESH_ATTEMPTS) {
          try {
            const newTokens = await refresh(refreshToken);
            if (newTokens) {
              lsRemove(BACKOFF_KEY);
              setServerDownUntil(0);
              updateAuthToken(newTokens.accessToken);
              lsSet('refreshToken', newTokens.refreshToken);
              setIsLoggedIn(true);

              const msg: TChannelMsg = {
                type: 'FORCE_LOGIN',
                accessToken: newTokens.accessToken,
              };
              AUTH_CHANNEL?.postMessage(msg);

              return 'ok';
            }
          } catch (err: any) {
            const status = err?.response?.status;
            const isNetworkError = !status;

            if (isNetworkError || (status >= 500 && status < 600)) {
              const until = now() + API_DOWN_RETRY_MS;
              lsSet(BACKOFF_KEY, String(until));
              setServerDownUntil(until);
              setAuthDegraded(true);
              setIsLoggedIn(true);

              const msg: TChannelMsg = { type: 'SERVER_DOWN', until };
              AUTH_CHANNEL?.postMessage(msg);
              lsSet('bc_event', JSON.stringify(msg));
              setTimeout(() => lsRemove('bc_event'), 200);

              setTimeout(() => authEventEmitter.emit('refreshTokens'), API_DOWN_RETRY_MS);
              return 'degraded';
            }

            // 4xx → neprodlužuj a zkusit další pokus (pokud byl např. 409 aj.), jinak ven
          }
          attempt++;
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        applyLoggedOutState(false);
        return 'logout';
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = task;
    return task;
  }, [refresh, updateAuthToken, applyLoggedOutState]);

  /* ------------------- Mount: ověření tokenů & initial flow ---------------- */

  useEffect(() => {
    if (!isBrowser) return;
    if (hasFetched.current) return;
    hasFetched.current = true;

    const refreshToken = lsGet('refreshToken');
    // Access tokens from older versions must never be restored from persistent storage.
    lsRemove('accessToken');
    lsRemove('accessTokenExp');

    // aktivní backoff → nerefreshuj hned na mountu, drž uživatele přihlášeného
    const backoffUntil = lsGetInt(BACKOFF_KEY, 0);
    if (now() < backoffUntil) {
      setServerDownUntil(backoffUntil);
      setAuthDegraded(true);
      if (refreshToken) {
        setIsLoggedIn(true);
      }
      setAuthReady(true);
      return;
    }

    void (async () => {
      try {
        if (refreshToken) {
          const result = await handleRefreshToken();
          if (result === 'degraded') {
            setAuthDegraded(true);
          } else if (result === 'logout') {
            applyLoggedOutState(true);
            return;
          }
        } else {
          logOutCleanup();
          setIsLoggedIn(false);
        }
      } finally {
        setAuthReady(true);
      }
    })();
  }, [handleRefreshToken, applyLoggedOutState, logOutCleanup]);

  useEffect(() => {
    setRequestRefresh(handleRefreshToken);
  }, [handleRefreshToken]);

  /* ---------------------------- Multi-tab synchronizace --------------------------- */

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const logoutDueToInactivity = (): void => {
      const msg: TChannelMsg = { type: 'FORCE_LOGOUT' };
      AUTH_CHANNEL?.postMessage(msg);
      lsSet('bc_event', JSON.stringify(msg));
      setTimeout(() => lsRemove('bc_event'), 200);

      console.log('INACTIVITY_LOGOUT');
      setInactivity(true);
      lsSet('inactivityLogout', 'true');
      logOut(true);
    };

    const scheduleInactivityCheck = (): void => {
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }

      if (!isLoggedIn) {
        return;
      }

      const lastActivityTime = lsGetInt('lastActivityTime', now());
      const delay = Math.max(INACTIVITY_LIMIT - (now() - lastActivityTime), 1_000);

      inactivityTimeout.current = setTimeout(() => {
        const latestActivityTime = lsGetInt('lastActivityTime', 0);
        if (latestActivityTime > 0 && now() - latestActivityTime < INACTIVITY_LIMIT) {
          scheduleInactivityCheck();
          return;
        }

        logoutDueToInactivity();
      }, delay);
    };

    const onActivity = (): void => {
      if (!isLoggedIn) {
        return;
      }

      const t = now();
      if (t - lastWrite.current > ACTIVITY_WRITE_THROTTLE_MS) {
        lsSet('lastActivityTime', t.toString());
        lastWrite.current = t;
      }

      scheduleInactivityCheck();
    };

    const onTabClose = (): void => {
      lsRemove('lastAddedPoint');
    };

    const onStorage = (e: StorageEvent): void => {
      // INACTIVITY SYNC
      if (e.key === 'lastActivityTime' && e.newValue) {
        scheduleInactivityCheck();
      }

      // Fallback „BroadcastChannel“ přes storage
      if (e.key === 'bc_event' && e.newValue) {
        try {
          const msg = JSON.parse(e.newValue) as TChannelMsg;

          if (msg.type === 'FORCE_LOGOUT') {
            applyLoggedOutState(true);
          } else if (msg.type === 'FORCE_LOGIN') {
            updateAuthToken(msg.accessToken);
            setIsLoggedIn(true);
            scheduleInactivityCheck();
          } else if (msg.type === 'SERVER_DOWN') {
            setServerDownUntil(msg.until);
            setAuthDegraded(true);
            setIsLoggedIn(true);
          }
        } catch {
          /* ignore */
        }
      }

      // LOGOUT (flag z jiného tabu)
      if (e.key === 'isLoggingOut' && e.newValue === 'true') {
        applyLoggedOutState(true);
      }

      // NO REFRESH TOKEN => LOG OUT
      if (e.key === 'refreshToken' && e.newValue === null) {
        applyLoggedOutState(true);
      }
    };

    // Native BroadcastChannel (pokud je k dispozici)
    if (AUTH_CHANNEL) {
      AUTH_CHANNEL.onmessage = (event: MessageEvent<TChannelMsg>) => {
        const data = event.data;

        if (!data) {
          return;
        }

        if (data.type === 'FORCE_LOGOUT') {
          applyLoggedOutState(true);
        } else if (data.type === 'FORCE_LOGIN') {
          updateAuthToken(data.accessToken);
          setIsLoggedIn(true);
          scheduleInactivityCheck();
        } else if (data.type === 'SERVER_DOWN') {
          setServerDownUntil(data.until);
          setAuthDegraded(true);
          setIsLoggedIn(true);
        }
      };
    }

    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity);
    window.addEventListener('visibilitychange', onActivity);
    window.addEventListener('beforeunload', onTabClose);
    window.addEventListener('storage', onStorage);

    authEventEmitter.on('refreshTokens', handleRefreshToken);
    onActivity();

    return () => {
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('visibilitychange', onActivity);
      window.removeEventListener('beforeunload', onTabClose);
      window.removeEventListener('storage', onStorage);
      authEventEmitter.removeListener('refreshTokens', handleRefreshToken);
      if (AUTH_CHANNEL) AUTH_CHANNEL.onmessage = null;
      if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    };
  }, [isLoggedIn, applyLoggedOutState, updateAuthToken, handleRefreshToken, logOut]);

  /* -------------------------------- Context -------------------------------- */

  const ctxValue = useMemo<TAuthContextValue>(
    () => ({
      isLoggedIn,
      authReady,
      inactivity,
      loginError,
      handleSetLoginError,
      logIn,
      logOut,
      isActionAllowed,
      permissions,
      user,
      loginErrorMsg,
      handleSetLoginErrorMsg,

      serverDownUntil,
      isServerDown,
      authDegraded,
    }),
    [
      isLoggedIn,
      authReady,
      inactivity,
      loginError,
      handleSetLoginError,
      logIn,
      logOut,
      isActionAllowed,
      permissions,
      user,
      loginErrorMsg,
      handleSetLoginErrorMsg,

      serverDownUntil,
      isServerDown,
      authDegraded,
    ]
  );

  return <AuthContext.Provider value={ctxValue}>{children}</AuthContext.Provider>;
};

const useAuthContext = (): TAuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthContextProvider');
  }
  return context;
};

export { AuthContextProvider, useAuthContext };
